import React from "react";
import { cropText } from "./cropText";

/**
 * CropPhenologyTimeline — the proof object for FarmTopia's "Egyptian crop
 * models, backed by researched agronomy" positioning.
 *
 * Renders a crop's phenology stages as a proportional bar plus per-stage
 * cards showing the day/month window, the FAO-56 crop coefficient (Kc), and
 * the expected NDVI band. The expected-NDVI band is what the platform's
 * satellite layer VERIFIES against — it does not drive the stage; the
 * time-from-phenology model does. The caption states that explicitly.
 *
 * Reused on /crops/:slug (full) and /try-your-field (compact, future).
 */

const STAGE_COLORS = ["#bbf7d0", "#86efac", "#22c55e", "#ca8a04", "#b45309"];

export default function CropPhenologyTimeline({ crop, lang = "EN", variant = "full" }) {
  const isAr = lang === "AR";
  const stages = Array.isArray(crop?.phenology) ? crop.phenology : [];
  const total =
    crop?.totalDays ||
    stages.reduce((s, p) => Math.max(s, p.startDay + p.durationDays), 0) ||
    1;

  if (stages.length === 0) return null;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="w-full">
      {/* Proportional duration bar */}
      <div className="flex w-full h-3 rounded-full overflow-hidden mb-4">
        {stages.map((stage, i) => (
          <div
            key={stage.key}
            style={{
              flexBasis: `${(stage.durationDays / total) * 100}%`,
              backgroundColor: STAGE_COLORS[i % STAGE_COLORS.length],
            }}
            title={cropText(stage.name, lang)}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Per-stage cards */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
          variant === "compact" ? "lg:grid-cols-3" : "lg:grid-cols-5"
        }`}
      >
        {stages.map((stage, i) => (
          <div
            key={stage.key}
            className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
          >
            <div
              className="h-1.5 rounded-full mb-2"
              style={{ backgroundColor: STAGE_COLORS[i % STAGE_COLORS.length] }}
            />
            <div className="text-sm font-semibold text-gray-900">
              {cropText(stage.name, lang)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {cropText(stage.window, lang)}
            </div>
            <div className="mt-2 text-lg font-bold text-emerald-700">
              Kc {Number(stage.kc).toFixed(2)}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              {isAr ? "NDVI المتوقع" : "Expected NDVI"}:{" "}
              <span className="font-medium">
                {Number(stage.ndvi.min).toFixed(2)} – {Number(stage.ndvi.max).toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Caption — states the verify-don't-drive posture explicitly */}
      <p className="mt-3 text-xs text-gray-500 leading-relaxed">
        {isAr
          ? "معامل المحصول (Kc) ونطاق NDVI المتوقع لكل مرحلة، مصدرهما نموذج المحصول العلمي. يتحقق مؤشر NDVI الفعلي من النموذج — ولا يقوده؛ المرحلة محسوبة بالزمن من مراحل نمو المحصول."
          : "Crop coefficient (Kc) and expected NDVI band per stage, from the science-based crop model. Live NDVI verifies the model — it does not drive it; the stage is computed by time from the crop's phenology."}
      </p>
    </div>
  );
}
